import { createFileRoute, notFound, useLocation } from "@tanstack/react-router";
import { helpSearch } from "../../src/lib/help-navigation";
import { DocumentContent } from "../components/document-content";
import { usePublishedData } from "../lib/published-data";
import { documentHead, loadPage, useShell, type LoadedPage } from "../lib/document-route";
import { helpHead } from "../lib/help-route";
import { loadHelpIndex } from "../platform/help";
import { HelpHome } from "../platform/help/help-pages";
import { isPlatformShell } from "../platform/shell";

/**
 * /help: Drate's help center on drate.app; on a creator's site, their own Help page (one of the
 * policy pages, shared/src/policy-pages.ts), drawn like their other pages.
 */
export const Route = createFileRoute("/help/")({
  validateSearch: helpSearch,
  loaderDeps: ({ search }) => search,
  loader: async ({ deps, parentMatchPromise }) => {
    if (!isPlatformShell((await parentMatchPromise).loaderData))
      return { kind: "creator" as const, data: await loadPage("/help", "missing") };
    const page = await loadHelpIndex({ data: deps });
    if (!page) throw notFound();
    return { kind: "help" as const, page };
  },
  head: ({ matches, loaderData, match }) =>
    loaderData?.kind === "creator"
      ? documentHead(matches, loaderData.data, match.pathname, {
          title: (shell) => `Page not found | ${shell.creator.name}`,
          description: () => "This page could not be found.",
        })
      : helpHead(matches, loaderData?.page),
  component: HelpRoute,
});

function HelpRoute() {
  const loaded = Route.useLoaderData();
  return loaded.kind === "creator" ? (
    <CreatorHelp initial={loaded.data} />
  ) : (
    <HelpHome page={loaded.page} />
  );
}

function CreatorHelp({ initial }: { initial: LoadedPage }) {
  const shell = useShell();
  const { data } = usePublishedData(initial, shell, useLocation().pathname, "page");
  return data.kind === "page" ? <DocumentContent data={data} shell={shell} /> : null;
}
